import { Component, inject, OnInit, OnDestroy, Input, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { NgbModal, NgbNavModule } from '@ng-bootstrap/ng-bootstrap';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, Validators } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import { CoreModule } from 'app/modules/core.module';
import { NumberPipe } from 'app/pipes/number.pipe';
import { dateValidator, RutValidator } from 'app/validators/iatuch.validators';
import { RequestService } from '@services/request.service';
import { CampusService } from '@services/campus.service';
import { PolicyService } from '@services/policy.service';
import { GroupOfPoliceService } from '@services/groupOfPolicies.service';
import { InsuredService } from '@services/insured.service';
import { BankService } from '@services/banks.service';
import { EntityService } from '@services/entity.service';
import { UserService } from '@services/core/user.service';
import { canApproveRequest, canEditRequest } from 'app/utils/role-rules';
import Swal from 'sweetalert2';
import { Subject, takeUntil } from 'rxjs';
import { RequestModel, RequestForm, Gender, Bank, AccountType, Group, Policy } from './review-request.models';

const Toast = Swal.mixin({
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 2000,
  timerProgressBar: true
})

@Component({
  selector: 'app-review-request',
  standalone: true,
  imports: [NgbNavModule, CommonModule, NgSelectComponent, CoreModule, FormsModule, NumberPipe],
  providers: [RequestService, EntityService, PolicyService, GroupOfPoliceService, InsuredService, BankService],
  templateUrl: './review-request.component.html',
  styleUrls: ['./review-request.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReviewRequestComponent implements OnInit, OnDestroy {
  @Input() requestModel: RequestModel
  @Input() requestId: string
  @Input() showHeader = false
  @Input() showFooter = true
  @Input() isAdmin = false
  @Output() updateRequest = new EventEmitter<boolean>()

  private destroy$ = new Subject<void>()
  private modalService = inject(NgbModal)
  private requestService = inject(RequestService)
  private entityService = inject(EntityService)
  private policyService = inject(PolicyService)
  private groupOfPolicyService = inject(GroupOfPoliceService)
  private insuredService = inject(InsuredService)
  private bankService = inject(BankService)
  private fb = inject(FormBuilder)
  private _userService = inject(UserService)
  private cdr = inject(ChangeDetectorRef)

  protected editRequest = false
  protected get isExclusion(): boolean {
    return this.requestModel?.movement?.priority === 4
  }
  protected get canApprove(): boolean {
    return canApproveRequest((this._userService.sharingRole() as any)?.priority)
  }
  protected get canEdit(): boolean {
    return canEditRequest((this._userService.sharingRole() as any)?.priority, (this.requestModel as any)?.state?.stage)
  }
  protected requestForm: FormGroup
  protected listPolicies: Policy[] = []
  protected showSpinner = true
  protected listGenders: Gender[] = []
  protected listOffices: any[] = []
  protected listGroups: Group[] = []
  protected listBanks: Bank[] = []
  protected listTypeAccount: AccountType[] = []
  protected listInsuredTypes: any[] = []

  ngOnInit(): void {
    if (this.requestId) {
      this.getRequest()
    }
    this.initForm()
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  private _buildInsuredGroup(): FormGroup {
    return this.fb.group({
      _id: [null],
      names: [null, Validators.required],
      surname: [null, Validators.required],
      lastname: [null, Validators.required],
      rut: [null, [Validators.required, Validators.pattern(/^\d{7,9}$/)]],
      dv: [null, Validators.required],
      birthdate: [null, [Validators.required, dateValidator.validateFutureDate]],
      genderId: [null, Validators.required],
      mail: [null],
      phone: [null],
    })
  }

  private _buildAccountGroup(): FormGroup {
    return this.fb.group({
      _id: [null],
      insuredId: [null],
      bankId: [null],
      accountTypeId: [null],
      number: [null],
    })
  }

  private _buildContractGroup(): FormGroup {
    return this.fb.group({
      date_finish: [null],
      policy: [null],
      group: [null],
      capital: [null],
      date_start: [null],
    })
  }

  private _buildTransferGroup(): FormGroup {
    return this.fb.group({
      relationshipId: [null, Validators.required],
      entityId: [null, Validators.required],
      policyId: [null, Validators.required],
      coverageId: [null, Validators.required],
      groupId: [null, Validators.required],
      capital: [null, Validators.required],
      dateStart: [null, Validators.required],
    })
  }

  private initForm(): void {
    this.requestForm = this.fb.group({
      _id: [null],
      dateRequest: [null, Validators.required],
      dateExport: [null],
      dateFinish: [null],
      insured: this._buildInsuredGroup(),
      account: this._buildAccountGroup(),
      contractId: [null],
      contract: this._buildContractGroup(),
      transfer: this._buildTransferGroup(),
      approved: [null],
    })
  }

  dismiss(): void {
    this.modalService.dismissAll()
  }

  saveChanges(): void {
    this.requestService.update(this.requestForm.value)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.updateRequest.emit(true)
          this.editRequest = false
          this.cdr.markForCheck()
          this.modalService.dismissAll()
          Toast.fire({ title: 'Se ha actualizado correctamente', icon: 'success' })
        },
        error: () => {
          Toast.fire({ title: 'Error al actualizar', icon: 'error' })
        }
      })
  }

  approval(decision: boolean): void {
    this.requestService.approvalWorkflow(this.requestModel, decision)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.updateRequest.emit(true)
          this.modalService.dismissAll()
          Toast.fire({ title: res.msg, icon: 'success' })
        },
        error: () => {
          Toast.fire({ title: 'Error al procesar la solicitud', icon: 'error' })
        }
      })
  }

  setEditRequest(): void {
    this.editRequest = true
    this._patchFormFromModel()
    if (this.isExclusion) {
      const transferGroup = this.requestForm.get('transfer') as FormGroup
      Object.keys(transferGroup.controls).forEach(key => {
        transferGroup.get(key)?.clearValidators()
        transferGroup.get(key)?.updateValueAndValidity()
      })
      return
    }
    this.loadSelectData()
    this.getAllPolicies()
    this.requestForm.get('contract.date_finish')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        this.requestForm.patchValue({ dateFinish: value }, { emitEvent: false })
      })
  }

  private _patchFormFromModel(): void {
    this.requestForm.patchValue({
      _id: this.requestModel._id,
      dateRequest: this.requestModel.dateRequest?.split('T')[0],
      dateExport: this.requestModel.dateExport?.split('T')[0],
      dateFinish: this.requestModel.dateFinish?.split('T')[0],
      insured: {
        ...this.requestModel.insured,
        birthdate: this.requestModel.insured?.birthdate?.split('T')[0],
        genderId: this.requestModel.insured?.genderId || this.requestModel.insured?.gender?._id,
      },
      account: {
        _id: this.requestModel.insured?.account?._id,
        insuredId: this.requestModel.insured?._id,
        bankId: this.requestModel.insured?.account?.bank?._id || this.requestModel.insured?.account?.bankId,
        accountTypeId: this.requestModel.insured?.account?.accountType?._id || this.requestModel.insured?.account?.type_account?._id || this.requestModel.insured?.account?.accountTypeId,
        number: this.requestModel.insured?.account?.number,
      },
      contract: {
        date_finish: this.requestModel.dateFinish?.split('T')[0],
        policy: this.requestModel.contract?.policy,
        group: this.requestModel.contract?.group,
        capital: this.requestModel.contract?.capital,
        date_start: this.requestModel.contract?.dateStart?.split('T')[0],
      },
      transfer: {
        ...this.requestModel,
        coverageId: this.requestModel.policy?.coverageId,
        dateStart: this.requestModel.dateStart?.split('T')[0],
      },
    })
  }

  private loadSelectData(): void {
    this.entityService.getAll().pipe(takeUntil(this.destroy$)).subscribe(r => { this.listOffices = r; this.cdr.markForCheck() })
    this.insuredService.getGenders().pipe(takeUntil(this.destroy$)).subscribe(r => { this.listGenders = r; this.cdr.markForCheck() })
    this.insuredService.getInsuredTypes().pipe(takeUntil(this.destroy$)).subscribe(r => { this.listInsuredTypes = r; this.cdr.markForCheck() })
    this.bankService.getAll().pipe(takeUntil(this.destroy$)).subscribe(r => { this.listBanks = r; this.cdr.markForCheck() })
    this.bankService.getTypeAccount().pipe(takeUntil(this.destroy$)).subscribe(r => { this.listTypeAccount = r; this.cdr.markForCheck() })
  }

  setDigitVerificator(): void {
    const rut = this.requestForm.get('insured.rut')?.value
    if (rut) {
      const dv = RutValidator.setDigitVerificator(rut)
      this.requestForm.patchValue({ insured: { dv } })
    }
  }

  cancel(): void {
    this.editRequest = false
    if(this.requestModel.account?.number){
      this.requestModel.account!.bank = this.listBanks.find(b => b._id == this.requestModel.account!.bank)
      this.requestModel.account!.type_account = this.listTypeAccount.find(ta => ta._id == this.requestModel.account!.type_account)
    }
  }

  getRequest(): void {
    this.requestService.getRequest(this.requestId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        this.requestModel = res
        this.cdr.markForCheck()
        Swal.close()
      })
  }

  getAllPolicies(): void {
    const entityId = this.requestForm.get('transfer.entityId')?.value
    if (entityId) {
      this.policyService.getByCampus(entityId)
        .pipe(takeUntil(this.destroy$))
        .subscribe(r => {
          this.listPolicies = r
          this.cdr.markForCheck()
          this.getAllGroups()
        })
    }
  }

  getAllGroups(): void {
    const policyId = this.requestForm.get('transfer.policyId')?.value
    if (policyId) {
      this.groupOfPolicyService.getByPolicy(policyId)
        .pipe(takeUntil(this.destroy$))
        .subscribe(r => { this.listGroups = r; this.cdr.markForCheck() })
    }
  }

  onEntityChange(): void {
    const entityId = this.requestForm.get('transfer.entityId')?.value
    if (entityId) {
      this.policyService.getByCampus(entityId)
        .pipe(takeUntil(this.destroy$))
        .subscribe(r => {
          this.listPolicies = r
          this.cdr.markForCheck()
          this.requestForm.patchValue({
            transfer: { policyId: null, groupId: null, capital: null }
          })
        })
    }
  }

  onPolicyChange(): void {
    const policyId = this.requestForm.get('transfer.policyId')?.value
    if (policyId) {
      const policy = this.listPolicies.find(p => p._id === policyId)
      if (policy?.coverageId) {
        this.requestForm.patchValue({ transfer: { coverageDestinyId: policy.coverageId } })
      }
      this.groupOfPolicyService.getByPolicy(policyId)
        .pipe(takeUntil(this.destroy$))
        .subscribe(r => { this.listGroups = r; this.cdr.markForCheck() })
    }
  }
}
